import type React from 'react';
import { Link } from 'react-router-dom';
import { X, Linkedin, Github } from 'lucide-react';

interface FooterLink {
  readonly label: string;
  readonly to?: string;          // React Router (internal route)
  readonly href?: string;        // Plain anchor (mailto, anchor, external)
}

interface FooterColumn {
  readonly heading: string;
  readonly links: ReadonlyArray<FooterLink>;
}

const COLUMNS: ReadonlyArray<FooterColumn> = [
  {
    heading: 'Product',
    links: [
      { label: 'Features', href: '#features' },
      { label: 'Pricing', href: '#pricing' },
      { label: 'How it works', href: '#how-it-works' },
    ],
  },
  {
    heading: 'Resources',
    links: [
      { label: 'FAQ', href: '#faq' },
      { label: 'Sample articles', to: '/coming-soon?topic=sample-articles' },
      { label: 'Case studies', to: '/coming-soon?topic=case-studies' },
      { label: 'Blog', to: '/coming-soon?topic=blog' },
    ],
  },
  {
    heading: 'Company',
    links: [
      { label: 'About', to: '/coming-soon?topic=about' },
      { label: 'Careers', to: '/coming-soon?topic=careers' },
      { label: 'Contact', href: 'mailto:support@vantagedraft.com' },
    ],
  },
  {
    heading: 'Legal',
    links: [
      { label: 'Privacy policy', to: '/legal/privacy' },
      { label: 'Terms of service', to: '/legal/terms' },
    ],
  },
];

function FooterLinkItem({ link }: { readonly link: FooterLink }): React.ReactElement {
  const className = 'hover:text-bottle-green transition-colors';

  if (link.to) {
    return (
      <Link to={link.to} className={className}>
        {link.label}
      </Link>
    );
  }

  return (
    <a href={link.href} className={className}>
      {link.label}
    </a>
  );
}

export const Footer: React.FC = () => {
  return (
    <footer className="bg-pressed-paper border-t border-aged-brass/20 py-20 px-6 text-charcoal">
      <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-12 mb-16">
        {COLUMNS.map((column) => (
          <div key={column.heading}>
            <h4 className="font-display font-bold mb-6 text-lg">{column.heading}</h4>
            <ul className="space-y-3 text-sm text-muted-warm">
              {column.links.map((link) => (
                <li key={link.label}>
                  <FooterLinkItem link={link} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="max-w-6xl mx-auto pt-8 border-t border-aged-brass/20 flex flex-col md:flex-row justify-between items-center gap-6">
        <div className="text-muted-warm text-sm">
          &copy; {new Date().getFullYear()} Vantage Draft. Built for content professionals by content professionals.
        </div>
        <div className="flex gap-6">
          <a
            href="https://twitter.com/vantagedraft"
            className="text-muted-warm hover:text-bottle-green transition-colors"
            aria-label="Follow Vantage Draft on X"
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </a>
          <a
            href="https://linkedin.com/company/vantagedraft"
            className="text-muted-warm hover:text-bottle-green transition-colors"
            aria-label="Follow Vantage Draft on LinkedIn"
          >
            <Linkedin className="w-5 h-5" aria-hidden="true" />
          </a>
          <a
            href="https://github.com/vantagedraft"
            className="text-muted-warm hover:text-bottle-green transition-colors"
            aria-label="Vantage Draft on GitHub"
          >
            <Github className="w-5 h-5" aria-hidden="true" />
          </a>
        </div>
      </div>
    </footer>
  );
};
